import { useState } from "react";
import {
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Badge,
  Avatar,
  Typography,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Divider,
  Chip,
  Fade,
  Slide,
  keyframes,
  alpha,
} from "@mui/material";
import { 
  Dashboard, 
  Logout, 
  People, 
  AccountBalance, 
  Analytics,
  TrendingUp,
  ContactMail,
  Menu,
  Settings,
  Inventory,
  Favorite,
  Payment
} from "@mui/icons-material";
import { useCookies } from "react-cookie";
import { useNavigate, useLocation } from "react-router-dom";

// Custom animations for Navbar component
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.pathname);

  const [, , removeCookie] = useCookies(["auth_token"]);
  const logout = () => {
    let date = new Date();
    date.setDate(1);
    date.setMonth(1);
    date.setFullYear(1960);
    removeCookie("auth_token", { path: "/", expires: date });
    navigate("/");
  };

  const navigationItems = [
    {
      id: '/customer/dashboard',
      label: 'Dashboard',
      icon: <Dashboard />,
      color: '#4CAF50',
      subtext: 'My Overview'
    },
    {
      id: '/customer/loans',
      label: 'My Loans',
      icon: <AccountBalance />,
      color: '#2196F3',
      subtext: 'Loan Management'
    }
  ];

  return (
    <AppBar
      position="static"
      sx={{ 
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "0 0 20px 20px",
        mx: 2,
        mt: 1,
        width: "calc(100% - 32px)"
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar sx={{ 
              background: "linear-gradient(45deg, #667eea, #764ba2)",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)"
            }}>
              💸
            </Avatar>
            <Fade in={true} timeout={1000}>
              <Typography variant="h6" sx={{ 
                color: "white", 
                fontWeight: "bold",
                background: "linear-gradient(45deg, #fff, #f0f0f0)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text"
              }}>
                Loan Portal
              </Typography>
            </Fade>
          </Box>

          <List sx={{ display: "flex", gap: 1 }}>
            {navigationItems.map((item, index) => {
              const isActive = activeTab === item.id;
              return (
                <ListItem key={item.id} disablePadding>
                  <Tooltip title={item.label} placement="bottom">
                    <ListItemButton
                      onClick={() => {
                        setActiveTab(item.id);
                        navigate(item.id);
                      }}
                      sx={{
                        borderRadius: "15px",
                        mx: 0.5,
                        background: isActive ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(10px)",
                        border: isActive ? "2px solid rgba(255,255,255,0.3)" : "2px solid transparent",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        transform: isActive ? "scale(1.05)" : "scale(1)",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          background: "rgba(255,255,255,0.15)",
                          transform: "scale(1.08)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                          transition: "left 0.5s",
                        },
                        "&:hover::before": {
                          left: "100%",
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: isActive ? "white" : "rgba(255,255,255,0.9)",
                        transition: "all 0.3s ease",
                        transform: isActive ? "scale(1.2)" : "scale(1)"
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <Slide direction="right" in={isActive} timeout={300}>
                        <ListItemText 
                          primary={item.label}
                          sx={{ 
                            color: "white",
                            "& .MuiListItemText-primary": {
                              fontSize: "0.95rem",
                              fontWeight: isActive ? "bold" : "medium"
                            }
                          }}
                        />
                      </Slide>
                      {isActive && (
                        <Chip 
                          label="Active" 
                          size="small" 
                          sx={{ 
                            background: "rgba(255,255,255,0.2)",
                            color: "white",
                            border: "1px solid rgba(255,255,255,0.3)",
                            ml: 1,
                            animation: `${pulse} 2s infinite`
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Badge badgeContent={3} color="error" sx={{ "& .MuiBadge-badge": { animation: `${pulse} 2s infinite` } }}>
            <ListItemButton 
              onClick={logout}
              sx={{
                borderRadius: "15px",
                background: "rgba(255,255,255,0.1)",
                border: "2px solid rgba(255,255,255,0.2)",
                color: "white",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(45deg, #ff6b6b, #ff8e8e)",
                  transform: "scale(1.05)",
                  boxShadow: "0 8px 25px rgba(255,107,107,0.3)"
                }
              }}
            >
              <Logout sx={{ mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                Logout
              </Typography>
            </ListItemButton>
          </Badge>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const AdminNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(location.pathname);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [, , removeCookie] = useCookies(["auth_token"]);
  const logout = () => {
    let date = new Date();
    date.setDate(1);
    date.setMonth(1);
    date.setFullYear(1960);
    removeCookie("auth_token", { path: "/", expires: date });
    navigate("/");
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const adminNavigationItems = [
    {
      id: '/admin/dashboard',
      label: 'Dashboard',
      icon: <Analytics />,
      color: '#4CAF50',
      notification: false,
      subLabel: 'Business Overview'
    },
    {
      id: '/admin/customers',
      label: 'Customers',
      icon: <People />,
      color: '#2196F3',
      notification: false,
      subLabel: 'Manage Clients'
    },
    {
      id: '/admin/leads',
      label: 'Leads',
      icon: <ContactMail />,
      color: '#00BCD4',
      notification: false,
      subLabel: 'Loan Applications'
    },
    {
      id: '/admin/products',
      label: 'Products',
      icon: <Inventory />,
      color: '#FF9800',
      notification: false,
      subLabel: 'Manage Products'
    },
    {
      id: '/admin/payments',
      label: 'Payments',
      icon: <Payment />,
      color: '#4CAF50',
      notification: false,
      subLabel: 'All Payments & Debits'
    },
    {
      id: '/admin/interest-requests',
      label: 'Interest Requests',
      icon: <Favorite />,
      color: '#E91E63',
      notification: false,
      subLabel: 'Customer Interests'
    },
    {
      id: '/admin/settings',
      label: 'Settings',
      icon: <Settings />,
      color: '#9C27B0',
      notification: false,
      subLabel: 'Account Settings'
    }
  ];

  const handleNavigation = (itemId: string) => {
    setActiveTab(itemId);
    navigate(itemId);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Drawer content component
  const drawerContent = (
    <Box sx={{ width: 280, height: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ 
          background: theme.palette.primary.main,
          width: 40,
          height: 40
        }}>
          📊
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Admin Console
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Whatzneu
          </Typography>
        </Box>
      </Box>
      <Divider />
      <List sx={{ pt: 1 }}>
        {adminNavigationItems.map((item) => {
          const isActive = activeTab === item.id;
          return (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => handleNavigation(item.id)}
                selected={isActive}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 0,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: isActive ? 'white' : 'inherit',
                  minWidth: 40
                }}>
                  {item.notification ? (
                    <Badge badgeContent="3" color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  secondary={isMobile ? item.subLabel : undefined}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ px: 2, pb: 2 }}>
        <ListItemButton
          onClick={logout}
          sx={{
            borderRadius: 1,
            backgroundColor: theme.palette.error.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={2}
        sx={{ 
          backgroundColor: '#ffffff',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          zIndex: theme.zIndex.drawer + 1,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar 
          sx={{ 
            justifyContent: "space-between", 
            px: { xs: 2, md: 4 },
            minHeight: { xs: 64, md: 72 },
            maxWidth: '100%',
          }}
        >
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2,
                color: theme.palette.text.primary,
              }}
            >
              <Menu />
            </IconButton>
          )}

          {/* Brand/Logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: { xs: 'auto', md: 200 } }}>
            <Avatar sx={{ 
              width: { xs: 40, md: 44 },
              height: { xs: 40, md: 44 },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            }}>
              📊
            </Avatar>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  color: theme.palette.text.primary,
                  lineHeight: 1.2,
                }}
              >
                Admin Console
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                Whatzneu
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, justifyContent: 'center', mx: 4 }}>
              {adminNavigationItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <Tooltip key={item.id} title={item.subLabel} placement="bottom" arrow>
                    <ListItemButton
                      onClick={() => handleNavigation(item.id)}
                      selected={isActive}
                      sx={{
                        borderRadius: 0,
                        px: 2.5,
                        py: 1.25,
                        minWidth: 'auto',
                        mx: 0.5,
                        transition: 'all 0.2s ease-in-out',
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: isActive ? 'white' : theme.palette.text.secondary,
                        minWidth: 36,
                      }}>
                        {item.notification ? (
                          <Badge badgeContent="3" color="error">
                            {item.icon}
                          </Badge>
                        ) : (
                          item.icon
                        )}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? 'white' : theme.palette.text.primary,
                        }}
                      />
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </Box>
          )}

          {/* Logout Button */}
          <Box sx={{ minWidth: { xs: 'auto', md: 80 }, display: 'flex', justifyContent: 'flex-end' }}>
            {isMobile ? (
              <IconButton
                color="inherit"
                onClick={logout}
                sx={{
                  backgroundColor: theme.palette.error.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.error.dark,
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Logout />
              </IconButton>
            ) : (
              <Tooltip title="Logout" arrow>
                <IconButton
                  onClick={logout}
                  sx={{
                    backgroundColor: theme.palette.error.main,
                    color: 'white',
                    boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`,
                    '&:hover': {
                      backgroundColor: theme.palette.error.dark,
                      transform: 'scale(1.05)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <Logout />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};
export { Navbar, AdminNavbar };
